/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI Inpainting & Mask Canvas Studio Complete View (Task 10.4.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  Hand,
  Undo2,
  Redo2,
  Trash2,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
  Check,
  AlertCircle,
  X,
  Sliders,
  Eye,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useTranslation } from '../i18n';
import { useInpaintingStore, INPAINT_MODELS, INPAINT_CREDIT_COST } from '../stores/useInpaintingStore';
import { useImageStudioStore } from '../stores/useImageStudioStore';
import { InpaintingCanvas } from '../components/media/InpaintingCanvas';

// Curated preset templates for instant inpainting demonstration
const SAMPLE_PRESETS = [
  {
    id: 'sample-interior',
    label: 'Modern Minimalist Living Room',
    url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1024&q=80',
    defaultPrompt: 'replace the coffee table with a sculptured white marble pedestal, dramatic studio lighting',
  },
  {
    id: 'sample-portrait',
    label: 'Studio Portrait Photography',
    url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=1024&q=80',
    defaultPrompt: 'add futuristic transparent cyberpunk neon sunglasses with subtle turquoise reflections',
  },
  {
    id: 'sample-nature',
    label: 'Serene Nature Landscape',
    url: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1024&q=80',
    defaultPrompt: 'add a cozy wooden rustic cabin by the lake with warm lights glowing from windows',
  },
];

interface InpaintingStudioViewProps {
  onNavigateToGallery?: () => void;
}

export const InpaintingStudioView: React.FC<InpaintingStudioViewProps> = ({ onNavigateToGallery }) => {
  const { t } = useTranslation();

  const {
    sourceImage,
    setSourceImage,
    activeTool,
    setActiveTool,
    brushSize,
    setBrushSize,
    brushHardness,
    setBrushHardness,
    maskOpacity,
    setMaskOpacity,
    binaryMaskDataUrl,
    zoomIn,
    zoomOut,
    resetZoom,
    triggerUndo,
    triggerRedo,
    triggerClear,
    triggerInvert,
    canUndo,
    canRedo,
    prompt,
    setPrompt,
    negativePrompt,
    setNegativePrompt,
    selectedModel,
    setSelectedModel,
    isProcessing,
    jobProgress,
    jobStage,
    resultImageUrl,
    errorMessage,
    executeInpaint,
    applyResultAsSource,
  } = useInpaintingStore();

  const { historyImages } = useImageStudioStore();

  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'editor' | 'compare'>('editor');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle local image file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload a valid image file (PNG, JPG, WebP)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setSourceImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Handle Download Image
  const handleDownload = (imageUrl: string, filename = 'sofia-inpaint-result.png') => {
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Save inpaint result to workspace image gallery
  const handleSaveToGallery = () => {
    if (!resultImageUrl) return;

    // Push into image studio store
    const imageStudio = useImageStudioStore.getState();
    const newGalleryItem = {
      id: `inpaint_${Date.now()}`,
      url: resultImageUrl,
      prompt: prompt || 'AI Inpaint Result',
      model: selectedModel,
      modelName: INPAINT_MODELS.find((m) => m.id === selectedModel)?.name || 'Inpaint AI',
      aspectRatio: '1:1',
      width: 1024,
      height: 1024,
      style: 'Inpainted',
      seed: Math.floor(Math.random() * 1000000),
      creditsUsed: INPAINT_CREDIT_COST,
      createdAt: new Date().toISOString(),
      isFavorite: false,
    };

    imageStudio.historyImages.unshift(newGalleryItem);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* 1. Header & Source Selector Toolbar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-bold text-xl text-foreground">
              {t('inpaint.title')}
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary uppercase tracking-wider">
              {t('inpaint.costBadge')}
            </span>
          </div>
          <p className="text-xs text-foreground-muted leading-relaxed">
            {t('inpaint.subtitle')}
          </p>
        </div>

        {/* Source Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3.5 py-2 rounded-xl bg-surface-elevated border border-surface-border hover:border-primary/50 text-foreground text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
          >
            <SophiaIcon name="vision" size={16} className="text-primary shrink-0" />
            <span>{t('inpaint.uploadImage')}</span>
          </button>

          <button
            type="button"
            onClick={() => (onNavigateToGallery ? onNavigateToGallery() : setIsGalleryModalOpen(true))}
            className="px-3.5 py-2 rounded-xl bg-surface-elevated border border-surface-border hover:border-primary/50 text-foreground text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
          >
            <SophiaIcon name="eye-recognition" size={16} className="text-accent shrink-0" />
            <span>{t('inpaint.chooseFromGallery')}</span>
          </button>
        </div>
      </div>

      {/* 2. Main Studio Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Canvas & Floating Toolbar (8 Cols) */}
        <div className="lg:col-span-8 space-y-4">
          {/* Canvas Floating Toolbar Ribbon */}
          <div className="p-3 rounded-2xl bg-surface border border-surface-border shadow-sm flex flex-wrap items-center justify-between gap-3">
            {/* Tool Selection Group */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-surface-elevated/80 border border-surface-border">
              <button
                type="button"
                onClick={() => setActiveTool('brush')}
                title={t('inpaint.brushTool')}
                className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                  activeTool === 'brush'
                    ? 'bg-primary text-primary-fg shadow-sm'
                    : 'text-foreground-muted hover:text-foreground hover:bg-surface'
                }`}
              >
                <SophiaIcon name="eye-recognition" size={15} className="shrink-0" />
                <span className="hidden sm:inline">{t('inpaint.brushTool')}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTool('eraser')}
                title={t('inpaint.eraserTool')}
                className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                  activeTool === 'eraser'
                    ? 'bg-primary text-primary-fg shadow-sm'
                    : 'text-foreground-muted hover:text-foreground hover:bg-surface'
                }`}
              >
                <SophiaIcon name="filter" size={15} className="shrink-0" />
                <span className="hidden sm:inline">{t('inpaint.eraserTool')}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTool('pan')}
                title={t('inpaint.panTool')}
                className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                  activeTool === 'pan'
                    ? 'bg-primary text-primary-fg shadow-sm'
                    : 'text-foreground-muted hover:text-foreground hover:bg-surface'
                }`}
              >
                <Hand className="w-4 h-4" />
                <span className="hidden sm:inline">{t('inpaint.panTool')}</span>
              </button>
            </div>

            {/* Brush Controls Group */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-elevated/80 border border-surface-border text-xs">
                <span className="text-foreground-muted text-[11px] whitespace-nowrap">
                  {t('inpaint.brushSize')}: <strong className="text-foreground">{brushSize}px</strong>
                </span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-16 sm:w-20 accent-primary cursor-pointer"
                />
              </div>

              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-elevated/80 border border-surface-border text-xs">
                <Sliders className="w-3.5 h-3.5 text-foreground-muted" />
                <span className="text-foreground-muted text-[11px] whitespace-nowrap">
                  {t('inpaint.brushHardness')}: <strong className="text-foreground">{Math.round(brushHardness * 100)}%</strong>
                </span>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={brushHardness}
                  onChange={(e) => setBrushHardness(Number(e.target.value))}
                  className="w-16 accent-primary cursor-pointer"
                />
              </div>

              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-elevated/80 border border-surface-border text-xs">
                <span className="text-foreground-muted text-[11px] whitespace-nowrap">
                  {t('inpaint.maskOpacity')}: <strong className="text-foreground">{Math.round(maskOpacity * 100)}%</strong>
                </span>
                <input
                  type="range"
                  min="0.2"
                  max="1"
                  step="0.1"
                  value={maskOpacity}
                  onChange={(e) => setMaskOpacity(Number(e.target.value))}
                  className="w-16 accent-primary cursor-pointer"
                />
              </div>
            </div>

            {/* History & Zoom Controls */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={!canUndo}
                onClick={triggerUndo}
                title={t('inpaint.undo')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated disabled:opacity-40 transition-colors"
              >
                <Undo2 className="w-4 h-4" />
              </button>

              <button
                type="button"
                disabled={!canRedo}
                onClick={triggerRedo}
                title={t('inpaint.redo')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated disabled:opacity-40 transition-colors"
              >
                <Redo2 className="w-4 h-4" />
              </button>

              <div className="w-[1px] h-4 bg-surface-border mx-1" />

              <button
                type="button"
                onClick={triggerInvert}
                title={t('inpaint.invertMask')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={triggerClear}
                title={t('inpaint.clearMask')}
                className="p-2 rounded-lg text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              <div className="w-[1px] h-4 bg-surface-border mx-1" />

              <button
                type="button"
                onClick={zoomIn}
                title={t('inpaint.zoomIn')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={zoomOut}
                title={t('inpaint.zoomOut')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <ZoomOut className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={resetZoom}
                title={t('inpaint.resetZoom')}
                className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Interactive Canvas Viewport */}
          <InpaintingCanvas />

          {/* Quick Notice */}
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface border border-surface-border text-xs text-foreground-muted">
            <SophiaIcon name="solution" size={15} className="text-primary flex-shrink-0" />
            <span>{t('inpaint.maskNotice')}</span>
          </div>
        </div>

        {/* Right Column: Generation Controls & Result Inspector (4 Cols) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Controls Card */}
          <div className="p-5 rounded-2xl bg-surface border border-surface-border shadow-sm space-y-5">
            <h3 className="font-heading font-bold text-sm text-foreground flex items-center gap-2">
              <SophiaIcon name="algorithm" size={16} className="text-primary flex-shrink-0" />
              <span>{t('inpaint.promptLabel')}</span>
            </h3>

            {/* Prompt Textarea */}
            <div className="space-y-1.5">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder={t('inpaint.promptPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl bg-surface-elevated border border-surface-border focus:border-primary focus:ring-1 focus:ring-primary text-xs text-foreground placeholder:text-foreground-muted/60 transition-all resize-none outline-none"
              />
            </div>

            {/* Negative Prompt */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-foreground-muted">
                {t('inpaint.negativePromptLabel')}
              </label>
              <input
                type="text"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder={t('inpaint.negativePromptPlaceholder')}
                className="w-full px-3.5 py-2 rounded-xl bg-surface-elevated border border-surface-border focus:border-primary focus:ring-1 focus:ring-primary text-xs text-foreground placeholder:text-foreground-muted/60 transition-all outline-none"
              />
            </div>

            {/* Model Selection */}
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-foreground-muted">
                {t('inpaint.modelLabel')}
              </label>
              <div className="space-y-2">
                {INPAINT_MODELS.map((model) => {
                  const isSelected = selectedModel === model.id;
                  return (
                    <div
                      key={model.id}
                      onClick={() => setSelectedModel(model.id)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-surface-border bg-surface-elevated/40 hover:border-surface-border/80'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-xs text-foreground">
                          {model.name}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-primary/10 text-primary">
                          {model.badge}
                        </span>
                      </div>
                      <p className="text-[11px] text-foreground-muted mt-1 leading-relaxed">
                        {model.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Binary Mask Preview */}
            {binaryMaskDataUrl && (
              <div className="p-3 rounded-xl bg-surface-elevated/60 border border-surface-border space-y-2">
                <div className="flex items-center justify-between text-[11px] font-semibold text-foreground-muted">
                  <span className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5 text-primary" />
                    <span>{t('inpaint.binaryMaskPreview')}</span>
                  </span>
                  <span className="text-[10px] text-emerald-500 font-bold">READY</span>
                </div>
                <div className="w-20 h-20 rounded-lg overflow-hidden border border-surface-border bg-black">
                  <img src={binaryMaskDataUrl} alt="Binary mask" className="w-full h-full object-cover" />
                </div>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{errorMessage}</span>
              </div>
            )}

            {/* Progress Bar when Generating */}
            {isProcessing && (
              <div className="space-y-2 p-3.5 rounded-xl bg-surface-elevated/60 border border-surface-border">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="text-foreground">{t('inpaint.processing')}</span>
                  <span className="text-primary">{jobProgress}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-surface-border/60 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 rounded-full"
                    style={{ width: `${jobProgress}%` }}
                  />
                </div>
                <p className="text-[11px] text-foreground-muted">{jobStage}</p>
              </div>
            )}

            {/* Generate Action Button */}
            <button
              type="button"
              disabled={isProcessing || !sourceImage}
              onClick={() => void executeInpaint()}
              className="w-full py-3 px-4 rounded-xl bg-primary text-primary-fg hover:opacity-90 font-bold text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-glow-primary/20"
            >
              <SophiaIcon name="algorithm" size={16} />
              <span>{t('inpaint.runInpaintBtn')}</span>
              <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-white/20">
                {t('inpaint.costBadge')}
              </span>
            </button>
          </div>

          {/* Result & Comparison Card */}
          {resultImageUrl && (
            <div className="p-5 rounded-2xl bg-surface border border-surface-border shadow-sm space-y-4 animate-fadeIn">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-bold text-sm text-foreground">
                  {t('inpaint.inpaintedResult')}
                </h4>
                <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface-elevated border border-surface-border text-[11px]">
                  <button
                    type="button"
                    onClick={() => setActiveTab('editor')}
                    className={`px-2 py-1 rounded-md font-medium transition-colors ${
                      activeTab === 'editor'
                        ? 'bg-primary text-primary-fg'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    {t('inpaint.inpaintedResult')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('compare')}
                    className={`px-2 py-1 rounded-md font-medium transition-colors ${
                      activeTab === 'compare'
                        ? 'bg-primary text-primary-fg'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    {t('inpaint.comparisonSplit')}
                  </button>
                </div>
              </div>

              {/* Result Preview Box */}
              {activeTab === 'editor' ? (
                <div className="relative rounded-xl overflow-hidden border border-surface-border aspect-square bg-black/20">
                  <img
                    src={resultImageUrl}
                    alt="Inpainted result"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 aspect-square">
                  <div className="relative rounded-xl overflow-hidden border border-surface-border bg-black/20">
                    <img
                      src={sourceImage || ''}
                      alt="Original"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white font-medium">
                      {t('inpaint.originalImage')}
                    </span>
                  </div>
                  <div className="relative rounded-xl overflow-hidden border border-surface-border bg-black/20">
                    <img
                      src={resultImageUrl}
                      alt="Inpainted"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded bg-primary text-primary-fg font-medium">
                      {t('inpaint.inpaintedResult')}
                    </span>
                  </div>
                </div>
              )}

              {/* Result Action Buttons */}
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={applyResultAsSource}
                  className="w-full py-2.5 px-3 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center justify-center gap-2 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-primary" />
                  <span>{t('inpaint.applyAsSource')}</span>
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(resultImageUrl)}
                    className="py-2.5 px-3 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>{t('inpaint.downloadResult')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSaveToGallery}
                    className="py-2.5 px-3 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    {saveSuccess ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-emerald-500">{t('common.saved')}</span>
                      </>
                    ) : (
                      <>
                        <SophiaIcon name="vision" size={14} className="text-accent" />
                        <span>{t('inpaint.saveToGallery')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3. Choose from Gallery Modal */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="relative w-full max-w-2xl rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-base text-foreground">
                  {t('inpaint.chooseFromGallery')}
                </h3>
                <p className="text-xs text-foreground-muted">
                  {t('inpaint.selectSourceDesc')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsGalleryModalOpen(false)}
                className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Preset Samples */}
            <div className="space-y-2">
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                Demo Presets
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SAMPLE_PRESETS.map((sample) => (
                  <div
                    key={sample.id}
                    onClick={() => {
                      setSourceImage(sample.url);
                      setPrompt(sample.defaultPrompt);
                      setIsGalleryModalOpen(false);
                    }}
                    className="group cursor-pointer rounded-xl overflow-hidden border border-surface-border hover:border-primary transition-all bg-surface-elevated/40 flex flex-col justify-between"
                  >
                    <div className="aspect-video w-full overflow-hidden bg-black/20">
                      <img
                        src={sample.url}
                        alt={sample.label}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2.5">
                      <span className="font-medium text-xs text-foreground block truncate">
                        {sample.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Generated Gallery Images */}
            {historyImages.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-surface-border">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                  Workspace Gallery ({historyImages.length})
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-1">
                  {historyImages.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => {
                        setSourceImage(img.url);
                        setIsGalleryModalOpen(false);
                      }}
                      className="group cursor-pointer rounded-xl overflow-hidden border border-surface-border hover:border-primary transition-all aspect-square bg-black/20 relative"
                    >
                      <img
                        src={img.url}
                        alt={img.prompt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
